import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAuth } from "firebase/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiUrl, apiUrlDev, apiUrlLocal } from "../../globalVariables";

const LEADERBOARD_PAGE_SIZE = 10;
const LEADERBOARD_MAX_HEIGHT_PX = 420;
const ORG_NAME_FETCH_DEBOUNCE_MS = 300;

const ADDY_APP_URL_PROD = "https://app.addy.so";
const ADDY_APP_URL_DEV = "https://devmail.addy.so";
function buildOrgEditUrl(env, orgId) {
  if (!orgId) return null;
  const base = env === "dev" ? ADDY_APP_URL_DEV : ADDY_APP_URL_PROD;
  return `${base}/organizations/${encodeURIComponent(orgId)}/edit`;
}
function buildLoanUrl(env, loanId) {
  if (!loanId) return null;
  const base = env === "dev" ? ADDY_APP_URL_DEV : ADDY_APP_URL_PROD;
  return `${base}/loans/${encodeURIComponent(loanId)}`;
}

const TRACE_PRESETS = [
  {
    id: "classifyDocumentV3",
    title: "Document Classification",
    short: "Classify",
    accent: "#7c3aed",
    accentSoft: "rgba(124, 58, 237, 0.10)",
    description: "Auto-tagging documents as they arrive (classifyDocumentV3)",
  },
  {
    id: "LoanApplication-step1PerformLoanTask-llm",
    title: "Step 1 — Perform Loan Task",
    short: "Step 1",
    accent: "#0ea5e9",
    accentSoft: "rgba(14, 165, 233, 0.10)",
    description: "Agent executing each task in the loan workflow",
  },
  {
    id: "LoanApplication-step3GetTheUIRepresentation-llm",
    title: "Step 3 — UI Representation",
    short: "Step 3",
    accent: "#10b981",
    accentSoft: "rgba(16, 185, 129, 0.10)",
    description: "Rendering task answers into UI structures",
  },
  {
    id: "extend-ai-ocr",
    title: "ExtendAI — Document OCR",
    short: "ExtendAI",
    accent: "#f59e0b",
    accentSoft: "rgba(245, 158, 11, 0.10)",
    description: "PDF parsing via ExtendAI ($0.012/page)",
    isExtendAi: true,
  },
];

const TIME_WINDOWS = [
  { value: "5m", label: "Last 5 min" },
  { value: "30m", label: "Last 30 min" },
  { value: "1h", label: "Last 1 hr" },
  { value: "6h", label: "Last 6 hrs" },
  { value: "24h", label: "Last 24 hrs" },
  { value: "7d", label: "Last 7 days" },
  { value: "custom", label: "Custom range" },
];

const palette = {
  bg: "#f4f6fb",
  surface: "#ffffff",
  border: "#e6e9f1",
  borderStrong: "#d6dae6",
  text: "#0f172a",
  textMuted: "#64748b",
  textDim: "#94a3b8",
  accent: "#4f46e5",
  danger: "#ef4444",
  ok: "#10b981",
};

const sx = {
  page: {
    minHeight: "100vh",
    background: palette.bg,
    color: palette.text,
    width: "100%",
    boxSizing: "border-box",
    padding: "24px 28px 64px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: 0,
    color: palette.textMuted,
    fontSize: 14,
    maxWidth: 760,
    marginTop: 4,
  },
  card: {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  },
  filterBar: {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  optionalRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px dashed ${palette.border}`,
  },
  fieldLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    height: 38,
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 10,
    padding: "0 12px",
    fontSize: 14,
    background: palette.surface,
    color: palette.text,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  primaryButton: {
    height: 38,
    border: "none",
    borderRadius: 10,
    padding: "0 18px",
    fontSize: 14,
    fontWeight: 600,
    background: palette.accent,
    color: "#fff",
    cursor: "pointer",
  },
  ghostButton: {
    height: 38,
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 10,
    padding: "0 14px",
    fontSize: 13,
    fontWeight: 500,
    background: palette.surface,
    color: palette.text,
    cursor: "pointer",
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginBottom: 24,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  sectionSubtitle: {
    color: palette.textMuted,
    fontSize: 13,
    marginTop: 2,
  },
  traceSection: {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 18,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
  },
  traceMiniGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    margin: "16px 0 18px",
  },
  miniStat: {
    background: "#fafbff",
    border: `1px solid ${palette.border}`,
    borderRadius: 12,
    padding: "12px 14px",
  },
  miniStatLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  miniStatValue: {
    fontSize: 22,
    fontWeight: 700,
    marginTop: 4,
    letterSpacing: "-0.01em",
  },
  miniStatSub: {
    color: palette.textMuted,
    fontSize: 12,
    marginTop: 2,
  },
  leaderboardWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
    gap: 16,
    marginTop: 20,
  },
  leaderboardCol: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 0,
  },
  leaderboardCard: {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
  },
  leaderboardHeader: {
    padding: "12px 14px",
    borderBottom: `1px solid ${palette.border}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  leaderboardAccentDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  leaderboardTitle: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  leaderboardSubtitle: {
    fontSize: 11,
    color: palette.textMuted,
    marginLeft: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 600,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: `1px solid ${palette.border}`,
    background: "#fafbff",
  },
  thRight: {
    padding: "10px 14px",
    textAlign: "right",
    fontSize: 11,
    fontWeight: 600,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: `1px solid ${palette.border}`,
    background: "#fafbff",
  },
  td: {
    padding: "10px 14px",
    borderBottom: `1px solid ${palette.border}`,
    verticalAlign: "middle",
  },
  tdRight: {
    padding: "10px 14px",
    borderBottom: `1px solid ${palette.border}`,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    verticalAlign: "middle",
  },
  monoChip: {
    display: "inline-block",
    background: "#f1f5f9",
    border: `1px solid ${palette.border}`,
    borderRadius: 6,
    padding: "1px 7px",
    fontSize: 12,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    color: palette.text,
  },
  emptyState: {
    padding: "32px 14px",
    textAlign: "center",
    color: palette.textMuted,
    fontSize: 13,
  },
  tableScroll: {
    maxHeight: LEADERBOARD_MAX_HEIGHT_PX,
    overflowY: "auto",
  },
  thButton: {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    color: palette.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  externalLink: {
    color: palette.textMuted,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 13,
    padding: 2,
    borderRadius: 4,
  },
  showMoreButton: {
    width: "100%",
    border: "none",
    borderTop: `1px solid ${palette.border}`,
    background: "#fafbff",
    color: palette.accent,
    fontSize: 12,
    fontWeight: 600,
    padding: "10px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  showMoreButtonDisabled: {
    color: palette.textDim,
    cursor: "default",
    background: "#fafbff",
  },
  orgNameWrap: {
    display: "inline-flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  orgNamePrimary: {
    fontWeight: 500,
    fontSize: 13,
    color: palette.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 220,
  },
  orgNameSecondary: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 11,
    color: palette.textDim,
    whiteSpace: "nowrap",
  },
};

function formatNumber(value = 0, maximumFractionDigits = 0) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(
    value || 0,
  );
}

function formatCompactNumber(value = 0) {
  if (!Number.isFinite(value) || value === 0) return "0";
  if (Math.abs(value) < 1000) return formatNumber(value);
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatCurrency(value = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 2 : 4,
  }).format(value || 0);
}

function formatLatency(value = 0) {
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

function formatPercent(value = 0) {
  if (!Number.isFinite(value)) return "—";
  if (value === 0) return "0%";
  if (value < 0.001) return "<0.1%";
  return `${(value * 100).toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function shortId(value, max = 10) {
  if (!value) return "—";
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

function getLocalDateTimeValue(value) {
  const offsetMs = value.getTimezoneOffset() * 60 * 1000;
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16);
}

function cleanQueryParams(params) {
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    cleaned[key] = value;
  });
  return cleaned;
}

function bucketLabel(timeWindow, isoKey) {
  const date = new Date(isoKey);
  if (Number.isNaN(date.getTime())) return "";
  if (timeWindow === "5m" || timeWindow === "30m" || timeWindow === "1h") {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (timeWindow === "6h" || timeWindow === "24h") {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function StatCard({ title, value, subtitle, accent, icon }) {
  return (
    <div
      style={{
        ...sx.card,
        padding: 18,
        borderTop: accent ? `3px solid ${accent}` : `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: palette.textMuted,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        <span>{title}</span>
        {icon && <span>{icon}</span>}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          marginTop: 6,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: 12,
            color: palette.textMuted,
            marginTop: 4,
            minHeight: 16,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub, accent }) {
  return (
    <div style={sx.miniStat}>
      <div style={sx.miniStatLabel}>{label}</div>
      <div style={{ ...sx.miniStatValue, color: accent || palette.text }}>
        {value}
      </div>
      {sub && <div style={sx.miniStatSub}>{sub}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <label style={sx.fieldLabel}>
        {label}
        {hint && (
          <span
            style={{
              marginLeft: 6,
              color: palette.textDim,
              textTransform: "none",
              fontWeight: 400,
              letterSpacing: 0,
            }}
          >
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function ChartTooltip({ active, payload, label, accent }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#0f172a",
        color: "#fff",
        padding: "8px 10px",
        borderRadius: 8,
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{ color: accent || "#a5b4fc" }}
        >
          {p.dataKey === "calls" ? "Calls" : p.dataKey}: {formatNumber(p.value)}
        </div>
      ))}
    </div>
  );
}

function TraceSection({ preset, data, timeWindow, onFilterByOrg, onFilterByLoan }) {
  const summary = data?.summary || {};
  const trend = (data?.trend || []).map((row) => ({
    name: bucketLabel(timeWindow, row.key),
    calls: row.traceCount,
    cost: row.totalCost,
  }));

  const traceCount = summary.traceCount || 0;
  const totalCost = summary.totalCost || 0;
  const avgLatency = summary.avgLatency || 0;
  const errorRate = summary.errorRate || 0;
  const avgCost = traceCount ? totalCost / traceCount : 0;
  const totalPages = summary.totalPages || 0;

  return (
    <div style={sx.traceSection}>
      <div style={sx.sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 10,
              height: 38,
              borderRadius: 4,
              background: preset.accent,
            }}
          />
          <div>
            <div style={sx.sectionTitle}>{preset.title}</div>
            <div style={sx.sectionSubtitle}>{preset.description}</div>
          </div>
        </div>
        <span style={{ ...sx.monoChip, color: palette.textMuted }}>
          {preset.id}
        </span>
      </div>

      <div style={sx.traceMiniGrid}>
        <MiniStat
          label={preset.isExtendAi ? "Parse calls" : "AI calls"}
          value={formatNumber(traceCount)}
          accent={preset.accent}
        />
        {preset.isExtendAi && (
          <MiniStat
            label="Pages parsed"
            value={formatNumber(totalPages)}
            sub={traceCount ? `${(totalPages / traceCount).toFixed(1)} pages/call` : ""}
          />
        )}
        <MiniStat
          label="Total cost"
          value={formatCurrency(totalCost)}
          sub={traceCount ? `${formatCurrency(avgCost)} per call` : ""}
        />
        <MiniStat
          label="Avg latency"
          value={formatLatency(avgLatency)}
          sub={summary.p95Latency ? `p95 ${formatLatency(summary.p95Latency)}` : ""}
        />
        <MiniStat
          label="Error rate"
          value={formatPercent(errorRate)}
          sub={`${formatNumber(summary.errorCount || 0)} errors`}
          accent={errorRate > 0.05 ? palette.danger : palette.text}
        />
      </div>

      <div
        style={{
          height: 200,
          background: preset.accentSoft,
          borderRadius: 12,
          padding: "12px 8px 4px",
        }}
      >
        {trend.length === 0 ? (
          <div style={sx.emptyState}>No activity in this window.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend} barCategoryGap={2}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="rgba(15, 23, 42, 0.06)"
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: palette.textMuted }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: palette.textMuted }}
                tickLine={false}
                axisLine={false}
                width={40}
                tickFormatter={(v) => formatCompactNumber(v)}
              />
              <Tooltip
                content={<ChartTooltip accent={preset.accent} />}
                cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
              />
              <Bar dataKey="calls" fill={preset.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function ExternalLinkButton({ href, label }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      title={label || "Open in Addy"}
      aria-label={label || "Open in Addy"}
      style={sx.externalLink}
    >
      ↗
    </a>
  );
}

function ShowMoreFooter({ visible, total, onShowMore, onShowLess, label }) {
  if (total <= LEADERBOARD_PAGE_SIZE) return null;
  const canShowMore = visible < total;
  const canShowLess = visible > LEADERBOARD_PAGE_SIZE;
  if (!canShowMore && !canShowLess) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 1,
        background: palette.border,
      }}
    >
      <button
        type="button"
        style={{
          ...sx.showMoreButton,
          ...(canShowMore ? null : sx.showMoreButtonDisabled),
          flex: 1,
        }}
        onClick={() => canShowMore && onShowMore()}
        disabled={!canShowMore}
      >
        {canShowMore
          ? `Show ${Math.min(LEADERBOARD_PAGE_SIZE, total - visible)} more · ${visible} of ${total}${label ? " " + label : ""}`
          : `Showing all ${total}${label ? " " + label : ""}`}
      </button>
      {canShowLess && (
        <button
          type="button"
          style={{ ...sx.showMoreButton, flex: "0 0 auto" }}
          onClick={onShowLess}
        >
          Collapse
        </button>
      )}
    </div>
  );
}

function AutoRunBadge({ active }) {
  if (!active) return null;
  return (
    <span
      title="autoRunProcessingChecklist is enabled for this organization"
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "#065f46",
        background: "rgba(16, 185, 129, 0.15)",
        border: "1px solid rgba(16, 185, 129, 0.4)",
        borderRadius: 4,
        lineHeight: 1.2,
      }}
    >
      Auto
    </span>
  );
}

function OrgIdCell({ orgId, displayMode, orgNameMap, orgAutoRunMap, env }) {
  if (!orgId || orgId === "Unknown") {
    return <span style={{ color: palette.textDim }}>—</span>;
  }
  const link = buildOrgEditUrl(env, orgId);
  const autoRun = !!(orgAutoRunMap && orgAutoRunMap[orgId]);
  if (displayMode === "name") {
    const resolved = orgNameMap[orgId];
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
      >
        <span style={sx.orgNameWrap}>
          <span style={sx.orgNamePrimary} title={resolved || orgId}>
            {resolved || (resolved === null ? "—" : shortId(orgId, 16))}
          </span>
          <span style={sx.orgNameSecondary} title={orgId}>
            {shortId(orgId, 16)}
          </span>
        </span>
        <AutoRunBadge active={autoRun} />
        <ExternalLinkButton href={link} label={`Open ${resolved || orgId}`} />
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span style={sx.monoChip} title={orgId}>
        {shortId(orgId, 16)}
      </span>
      <AutoRunBadge active={autoRun} />
      <ExternalLinkButton href={link} label={`Open ${orgId}`} />
    </span>
  );
}

function LoanIdCell({ loanId, env }) {
  if (!loanId || loanId === "Unknown") {
    return <span style={{ color: palette.textDim }}>—</span>;
  }
  const link = buildLoanUrl(env, loanId);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span style={sx.monoChip} title={loanId}>
        {shortId(loanId, 14)}
      </span>
      <ExternalLinkButton href={link} label={`Open loan ${loanId}`} />
    </span>
  );
}

function OrgHeaderToggle({ displayMode, onToggle }) {
  const isName = displayMode === "name";
  return (
    <button
      type="button"
      onClick={onToggle}
      style={sx.thButton}
      title={
        isName
          ? "Click to show Org IDs"
          : "Click to show Org Names (resolved on demand)"
      }
    >
      {isName ? "Org Name" : "Org ID"}
      <span style={{ fontSize: 9, opacity: 0.7 }}>⇅</span>
    </button>
  );
}

function OrgLeaderboard({
  preset,
  rows = [],
  onFilterByOrg,
  displayMode,
  onToggleDisplay,
  orgNameMap,
  orgAutoRunMap,
  env,
}) {
  const [visible, setVisible] = useState(LEADERBOARD_PAGE_SIZE);
  useEffect(() => setVisible(LEADERBOARD_PAGE_SIZE), [rows.length, preset.id]);
  const total = rows.length;
  const visibleRows = rows.slice(0, visible);

  return (
    <div style={sx.leaderboardCard}>
      <div style={sx.leaderboardHeader}>
        <div
          style={{ ...sx.leaderboardAccentDot, background: preset.accent }}
        />
        <div style={sx.leaderboardTitle}>
          Top organizations · {preset.short}
        </div>
        <div style={sx.leaderboardSubtitle}>by # of AI calls</div>
      </div>
      {total > 0 ? (
        <>
          <div
            style={
              visible > LEADERBOARD_PAGE_SIZE ? sx.tableScroll : undefined
            }
          >
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>
                    <OrgHeaderToggle
                      displayMode={displayMode}
                      onToggle={onToggleDisplay}
                    />
                  </th>
                  <th style={sx.thRight}>Calls</th>
                  <th style={sx.thRight}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => onFilterByOrg && onFilterByOrg(row.key)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={sx.td}>
                      <OrgIdCell
                        orgId={row.key}
                        displayMode={displayMode}
                        orgNameMap={orgNameMap}
                        orgAutoRunMap={orgAutoRunMap}
                        env={env}
                      />
                    </td>
                    <td style={sx.tdRight}>{formatNumber(row.traceCount)}</td>
                    <td style={sx.tdRight}>{formatCurrency(row.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMoreFooter
            visible={visibleRows.length}
            total={total}
            onShowMore={() => setVisible((n) => n + LEADERBOARD_PAGE_SIZE)}
            onShowLess={() => setVisible(LEADERBOARD_PAGE_SIZE)}
            label="orgs"
          />
        </>
      ) : (
        <div style={sx.emptyState}>No data in this window.</div>
      )}
    </div>
  );
}

function LoanLeaderboard({
  preset,
  rows = [],
  onFilterByLoan,
  displayMode,
  onToggleDisplay,
  orgNameMap,
  orgAutoRunMap,
  env,
}) {
  const [visible, setVisible] = useState(LEADERBOARD_PAGE_SIZE);
  useEffect(() => setVisible(LEADERBOARD_PAGE_SIZE), [rows.length, preset.id]);
  const total = rows.length;
  const visibleRows = rows.slice(0, visible);

  return (
    <div style={sx.leaderboardCard}>
      <div style={sx.leaderboardHeader}>
        <div
          style={{ ...sx.leaderboardAccentDot, background: preset.accent }}
        />
        <div style={sx.leaderboardTitle}>Top loans · {preset.short}</div>
        <div style={sx.leaderboardSubtitle}>by # of AI calls</div>
      </div>
      {total > 0 ? (
        <>
          <div
            style={
              visible > LEADERBOARD_PAGE_SIZE ? sx.tableScroll : undefined
            }
          >
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>Loan ID</th>
                  <th style={sx.th}>
                    <OrgHeaderToggle
                      displayMode={displayMode}
                      onToggle={onToggleDisplay}
                    />
                  </th>
                  <th style={sx.thRight}>Calls</th>
                  <th style={sx.thRight}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => onFilterByLoan && onFilterByLoan(row.key)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={sx.td}>
                      <LoanIdCell loanId={row.key} env={env} />
                    </td>
                    <td style={sx.td}>
                      {row.organizationId ? (
                        <OrgIdCell
                          orgId={row.organizationId}
                          displayMode={displayMode}
                          orgNameMap={orgNameMap}
                          orgAutoRunMap={orgAutoRunMap}
                          env={env}
                        />
                      ) : row.organizationCount > 1 ? (
                        <span
                          style={{ color: palette.textDim, fontSize: 12 }}
                        >
                          {row.organizationCount} orgs
                        </span>
                      ) : (
                        <span style={{ color: palette.textDim }}>—</span>
                      )}
                    </td>
                    <td style={sx.tdRight}>{formatNumber(row.traceCount)}</td>
                    <td style={sx.tdRight}>{formatCurrency(row.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMoreFooter
            visible={visibleRows.length}
            total={total}
            onShowMore={() => setVisible((n) => n + LEADERBOARD_PAGE_SIZE)}
            onShowLess={() => setVisible(LEADERBOARD_PAGE_SIZE)}
            label="loans"
          />
        </>
      ) : (
        <div style={sx.emptyState}>No data in this window.</div>
      )}
    </div>
  );
}

function HotspotCard({ title, accent, primaryLabel, primaryValue, rows }) {
  return (
    <div
      style={{
        ...sx.card,
        padding: 18,
        borderTop: `3px solid ${accent}`,
      }}
    >
      <div
        style={{
          color: palette.textMuted,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          marginTop: 6,
          letterSpacing: "-0.02em",
        }}
      >
        {primaryValue}
      </div>
      <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
        {primaryLabel}
      </div>
      {rows && rows.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: `1px dashed ${palette.border}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {rows.slice(0, 3).map((row) => (
            <div
              key={row.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
              }}
            >
              <span
                style={{
                  color: palette.textMuted,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {row.label}
                {row.autoRun && <AutoRunBadge active={true} />}
              </span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LangfuseAnalyticsPage({ onReady }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [environment, setEnvironment] = useState("prod");
  const [timeWindow, setTimeWindow] = useState("1h");
  const [organizationId, setOrganizationId] = useState("");
  const [loanId, setLoanId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [fromDateTime, setFromDateTime] = useState(
    getLocalDateTimeValue(new Date(Date.now() - 60 * 60 * 1000)),
  );
  const [toDateTime, setToDateTime] = useState(
    getLocalDateTimeValue(new Date()),
  );
  const [orgDisplayMode, setOrgDisplayMode] = useState("id");
  const [orgNameMap, setOrgNameMap] = useState({});
  const [orgAutoRunMap, setOrgAutoRunMap] = useState({});
  const orgNamesPendingRef = useRef(new Set());
  const orgNamesScheduledRef = useRef(null);

  // Allow deep-links (e.g. from the Org 360 dashboard) to pre-filter this page
  // to a single organization: /langfuse-analytics?org=<id>&env=dev
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orgParam = params.get("org") || params.get("organizationId");
    const envParam = params.get("env");
    if (orgParam) setOrganizationId(orgParam.trim());
    if (envParam === "dev" || envParam === "prod") setEnvironment(envParam);
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryParams = useMemo(() => {
    const customRange =
      timeWindow === "custom"
        ? {
            fromTimestamp: new Date(fromDateTime).toISOString(),
            toTimestamp: new Date(toDateTime).toISOString(),
          }
        : {};

    return cleanQueryParams({
      environment,
      timeWindow,
      organizationId: organizationId.trim(),
      loanId: loanId.trim(),
      taskId: taskId.trim(),
      rowLimit: 100,
      ...customRange,
    });
  }, [
    environment,
    timeWindow,
    organizationId,
    loanId,
    taskId,
    fromDateTime,
    toDateTime,
  ]);

  const fetchExtendAiAnalytics = useCallback(
    async (baseUrl, userId) => {
      try {
        const url = new URL(
          `${baseUrl}/admin/observability/extend-ai-analytics`,
        );
        Object.entries({ ...queryParams, userId }).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
        const resp = await fetch(url.toString());
        if (!resp.ok) return null;
        const json = await resp.json();
        if (!json.success) return null;
        return json;
      } catch (_e) {
        return null;
      }
    },
    [queryParams],
  );

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) {
        throw new Error("Admin user not found. Please refresh and sign in again.");
      }

      const isLocalhost =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");
      const baseUrls = isLocalhost ? [apiUrlLocal, apiUrlDev, apiUrl] : [apiUrl];
      let lastError = "Failed to fetch Langfuse analytics";
      let loaded = false;

      for (const baseUrl of baseUrls) {
        try {
          const url = new URL(`${baseUrl}/admin/observability/langfuse-analytics`);
          Object.entries({ ...queryParams, userId }).forEach(([key, value]) => {
            url.searchParams.set(key, value);
          });

          const controller = new AbortController();
          const timeout = setTimeout(
            () => controller.abort(),
            baseUrl.includes("localhost") ? 150000 : 120000,
          );
          const response = await fetch(url.toString(), {
            signal: controller.signal,
          });
          clearTimeout(timeout);
          const responseText = await response.text();

          let json = null;
          try {
            json = JSON.parse(responseText);
          } catch (_parseError) {
            lastError = responseText.trim().startsWith("<!DOCTYPE")
              ? `Langfuse analytics returned HTML from ${baseUrl}.`
              : `Langfuse analytics returned non-JSON from ${baseUrl}.`;
            continue;
          }

          if (!response.ok || !json.success) {
            lastError = json.reason || `Langfuse analytics failed on ${baseUrl}`;
            continue;
          }

          setData(json);
          loaded = true;

          // Fire-and-forget: fetch ExtendAI analytics in parallel
          fetchExtendAiAnalytics(baseUrl, userId).then((extendData) => {
            if (extendData) {
              setData((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  byTraceName: {
                    ...prev.byTraceName,
                    "extend-ai-ocr": extendData,
                  },
                };
              });
            }
          });

          break;
        } catch (innerError) {
          lastError =
            innerError?.name === "AbortError"
              ? `Langfuse analytics timed out at ${baseUrl}`
              : innerError?.message || `Could not reach ${baseUrl}`;
        }
      }

      if (!loaded) {
        throw new Error(lastError);
      }
    } catch (error) {
      console.error("Failed to fetch Langfuse analytics:", error);
      toast.error(error.message || "Failed to fetch Langfuse analytics");
    } finally {
      setIsLoading(false);
      if (onReady) onReady();
    }
  }, [queryParams, onReady, fetchExtendAiAnalytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // When the env tab changes, the same Org ID may resolve differently in the
  // dev vs prod Firestore. Drop the cache so we re-resolve.
  useEffect(() => {
    setOrgNameMap({});
    setOrgAutoRunMap({});
    orgNamesPendingRef.current = new Set();
  }, [environment]);

  const fetchOrgNames = useCallback(
    async (idsToFetch) => {
      if (!idsToFetch || idsToFetch.length === 0) return;
      try {
        const userId = getAuth()?.currentUser?.uid;
        if (!userId) return;
        const isLocalhost =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        const baseUrls = isLocalhost
          ? [apiUrlLocal, apiUrlDev, apiUrl]
          : [apiUrl];
        for (const baseUrl of baseUrls) {
          try {
            const url = new URL(
              `${baseUrl}/admin/observability/organization-names`,
            );
            url.searchParams.set("ids", idsToFetch.join(","));
            url.searchParams.set("userId", userId);
            const resp = await fetch(url.toString());
            if (!resp.ok) continue;
            const json = await resp.json();
            if (!json.success) continue;
            setOrgNameMap((prev) => {
              const next = { ...prev };
              for (const id of idsToFetch) {
                next[id] =
                  Object.prototype.hasOwnProperty.call(json.names, id)
                    ? json.names[id]
                    : null;
              }
              return next;
            });
            setOrgAutoRunMap((prev) => {
              const next = { ...prev };
              for (const id of idsToFetch) {
                next[id] = !!(json.autoRun && json.autoRun[id]);
              }
              return next;
            });
            for (const id of idsToFetch) {
              orgNamesPendingRef.current.delete(id);
            }
            return;
          } catch (_e) {
            /* try next baseUrl */
          }
        }
      } catch (error) {
        console.error("Failed to fetch organization names:", error);
      }
    },
    [],
  );

  const summary = data?.summary || {};
  const byTraceName = data?.byTraceName || {};
  const orgsLeader = data?.leaderboards?.organizations || [];
  const loansLeader = data?.leaderboards?.loans || [];
  const fetchDiag = data?.diagnostics?.fetch || {};

  // When user switches to Org Name mode, schedule a debounced batch lookup of
  // every Org ID that appears anywhere in the response (so all leaderboards
  // see resolved names without N round-trips per row).
  useEffect(() => {
    if (!data) return;
    const idsSet = new Set();
    Object.values(byTraceName).forEach((bucket) => {
      (bucket?.topOrganizations || []).forEach((row) => {
        if (row.key && row.key !== "Unknown") idsSet.add(row.key);
      });
      (bucket?.topLoans || []).forEach((row) => {
        if (row.organizationId) idsSet.add(row.organizationId);
        (row.organizationIds || []).forEach((id) => idsSet.add(id));
      });
    });
    (orgsLeader || []).forEach((row) => {
      if (row.key && row.key !== "Unknown") idsSet.add(row.key);
    });
    (loansLeader || []).forEach((row) => {
      if (row.organizationId) idsSet.add(row.organizationId);
    });
    const idsToFetch = Array.from(idsSet).filter(
      (id) =>
        !Object.prototype.hasOwnProperty.call(orgNameMap, id) &&
        !orgNamesPendingRef.current.has(id),
    );
    if (idsToFetch.length === 0) return;
    idsToFetch.forEach((id) => orgNamesPendingRef.current.add(id));
    if (orgNamesScheduledRef.current) {
      clearTimeout(orgNamesScheduledRef.current);
    }
    orgNamesScheduledRef.current = setTimeout(() => {
      fetchOrgNames(idsToFetch);
    }, ORG_NAME_FETCH_DEBOUNCE_MS);
  }, [
    orgDisplayMode,
    data,
    byTraceName,
    orgsLeader,
    loansLeader,
    orgNameMap,
    fetchOrgNames,
  ]);

  const toggleOrgDisplay = useCallback(() => {
    setOrgDisplayMode((mode) => (mode === "id" ? "name" : "id"));
  }, []);

  const coverageSubtitle = useMemo(() => {
    if (!data) return "Fetching…";
    const total = fetchDiag.totalTracesInWindow;
    const sampled = fetchDiag.sampledTraces;
    const stopped = fetchDiag.stoppedAtDeadline;
    const scheduled = fetchDiag.chunksScheduled;
    const processed = fetchDiag.chunksProcessed;
    const partialWindow =
      Number.isFinite(scheduled) &&
      Number.isFinite(processed) &&
      processed < scheduled;

    if (!Number.isFinite(total) || total <= 0) return "All matching traces";
    if (partialWindow) {
      const pct = Math.round((processed / scheduled) * 100);
      return `${pct}% of window scanned${stopped ? " · narrow window for full" : ""}`;
    }
    const ratio = Number.isFinite(sampled) ? sampled / total : 1;
    if (ratio >= 0.95) return "Full coverage of window";
    return `${Math.round(ratio * 100)}% sample of window`;
  }, [data, fetchDiag]);

  const topOrg = orgsLeader[0];
  const topLoan = loansLeader[0];
  const extendAiSummary = byTraceName["extend-ai-ocr"]?.summary || {};
  const extendAiCalls = extendAiSummary.traceCount || 0;
  const extendAiCost = extendAiSummary.totalCost || 0;
  const totalCalls = (summary.traceCount || 0) + extendAiCalls;
  const totalCost = (summary.totalCost || 0) + extendAiCost;

  const setOrgFilter = (value) => {
    setOrganizationId(value || "");
  };
  const setLoanFilter = (value) => {
    setLoanId(value || "");
  };

  return (
    <div className="LangfuseAnalyticsPageContainer" style={sx.page}>
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={sx.header}>
        <div>
          <h1 style={sx.title}>AI Usage Analytics</h1>
          <p style={sx.subtitle}>
            Per-workflow visibility into Langfuse traces. Find who is driving
            cost, where calls are spiking, and which loans/orgs need attention.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            style={sx.ghostButton}
            onClick={() => fetchAnalytics()}
            disabled={isLoading}
          >
            {isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div style={sx.filterBar}>
        <div style={sx.filterRow}>
          <Field label="Environment">
            <select
              style={sx.input}
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
            >
              <option value="prod">Production</option>
              <option value="dev">Development</option>
            </select>
          </Field>
          <Field label="Time window">
            <select
              style={sx.input}
              value={timeWindow}
              onChange={(event) => setTimeWindow(event.target.value)}
            >
              {TIME_WINDOWS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          {timeWindow === "custom" && (
            <>
              <Field label="From">
                <input
                  type="datetime-local"
                  style={sx.input}
                  value={fromDateTime}
                  onChange={(event) => setFromDateTime(event.target.value)}
                />
              </Field>
              <Field label="To">
                <input
                  type="datetime-local"
                  style={sx.input}
                  value={toDateTime}
                  onChange={(event) => setToDateTime(event.target.value)}
                />
              </Field>
            </>
          )}
        </div>

        <div style={sx.optionalRow}>
          <Field label="Organization ID" hint="optional">
            <input
              style={sx.input}
              value={organizationId}
              placeholder="e.g. pJO8Z4tt9zyNG0bQvZFY"
              onChange={(event) => setOrganizationId(event.target.value)}
            />
          </Field>
          <Field label="Loan ID" hint="optional">
            <input
              style={sx.input}
              value={loanId}
              placeholder="e.g. fnJGr6XYZdIE2ufUvGpA"
              onChange={(event) => setLoanId(event.target.value)}
            />
          </Field>
          <Field label="Task ID" hint="optional">
            <input
              style={sx.input}
              value={taskId}
              placeholder="e.g. taskAbc123"
              onChange={(event) => setTaskId(event.target.value)}
            />
          </Field>
          {(organizationId || loanId || taskId) && (
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                type="button"
                style={sx.ghostButton}
                onClick={() => {
                  setOrganizationId("");
                  setLoanId("");
                  setTaskId("");
                }}
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={sx.heroGrid}>
        <StatCard
          title="Total AI calls"
          value={formatNumber(totalCalls)}
          subtitle={coverageSubtitle}
          accent={palette.accent}
        />
        <StatCard
          title="Total cost"
          value={formatCurrency(totalCost)}
          subtitle={
            totalCalls
              ? `${formatCurrency(totalCost / totalCalls)} per call`
              : "—"
          }
          accent="#f59e0b"
        />
        <HotspotCard
          title="Heaviest organization"
          accent="#7c3aed"
          primaryValue={
            topOrg && topOrg.key !== "Unknown" ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
                title={`${orgNameMap[topOrg.key] || ""}\n${topOrg.key}`.trim()}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {orgNameMap[topOrg.key] || shortId(topOrg.key, 18)}
                </span>
                <AutoRunBadge active={!!orgAutoRunMap[topOrg.key]} />
              </span>
            ) : (
              "—"
            )
          }
          primaryLabel={
            topOrg
              ? `${formatNumber(topOrg.traceCount)} calls · ${formatCurrency(topOrg.totalCost)}${orgNameMap[topOrg.key] ? ` · ${shortId(topOrg.key, 12)}` : ""}`
              : "No data"
          }
          rows={(orgsLeader || []).slice(1, 4).map((row) => ({
            label: orgNameMap[row.key] || shortId(row.key, 14),
            value: `${formatNumber(row.traceCount)} · ${formatCurrency(row.totalCost)}`,
            autoRun: !!orgAutoRunMap[row.key],
          }))}
        />
        <HotspotCard
          title="Heaviest loan"
          accent="#0ea5e9"
          primaryValue={
            topLoan && topLoan.key !== "Unknown"
              ? shortId(topLoan.key, 16)
              : "—"
          }
          primaryLabel={
            topLoan
              ? `${formatNumber(topLoan.traceCount)} calls · ${formatCurrency(topLoan.totalCost)}${topLoan.organizationId ? ` · org ${shortId(topLoan.organizationId, 10)}` : ""}`
              : "No data"
          }
          rows={(loansLeader || []).slice(1, 4).map((row) => ({
            label: shortId(row.key, 14),
            value: `${formatNumber(row.traceCount)} · ${formatCurrency(row.totalCost)}`,
          }))}
        />
      </div>

      {TRACE_PRESETS.map((preset) => (
        <TraceSection
          key={preset.id}
          preset={preset}
          data={byTraceName[preset.id]}
          timeWindow={timeWindow}
          onFilterByOrg={setOrgFilter}
          onFilterByLoan={setLoanFilter}
        />
      ))}

      <div
        style={{
          ...sx.sectionHeader,
          marginTop: 28,
          marginBottom: 8,
        }}
      >
        <div>
          <div style={sx.sectionTitle}>Leaderboards</div>
          <div style={sx.sectionSubtitle}>
            Click a row to filter the entire dashboard by that org or loan.
          </div>
        </div>
      </div>

      <div style={sx.leaderboardWrap}>
        <div style={sx.leaderboardCol}>
          {TRACE_PRESETS.map((preset) => (
            <OrgLeaderboard
              key={preset.id}
              preset={preset}
              rows={byTraceName[preset.id]?.topOrganizations}
              onFilterByOrg={setOrgFilter}
              displayMode={orgDisplayMode}
              onToggleDisplay={toggleOrgDisplay}
              orgNameMap={orgNameMap}
              orgAutoRunMap={orgAutoRunMap}
              env={environment}
            />
          ))}
        </div>
        <div style={sx.leaderboardCol}>
          {TRACE_PRESETS.map((preset) => (
            <LoanLeaderboard
              key={preset.id}
              preset={preset}
              rows={byTraceName[preset.id]?.topLoans}
              onFilterByLoan={setLoanFilter}
              displayMode={orgDisplayMode}
              onToggleDisplay={toggleOrgDisplay}
              orgNameMap={orgNameMap}
              orgAutoRunMap={orgAutoRunMap}
              env={environment}
            />
          ))}
        </div>
      </div>

      <style>{`
        .LangfuseAnalyticsPageContainer input:focus,
        .LangfuseAnalyticsPageContainer select:focus {
          border-color: ${palette.accent};
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
        }
        .LangfuseAnalyticsPageContainer table tbody tr:hover {
          background: rgba(79, 70, 229, 0.04);
        }
        .LangfuseAnalyticsPageContainer table tbody tr:last-child td {
          border-bottom: none;
        }
        @media (max-width: 720px) {
          .LangfuseAnalyticsPageContainer { padding: 16px !important; }
        }
      `}</style>
    </div>
  );
}

export default LangfuseAnalyticsPage;
